import React from "react";
import {
    AppBar,
    Toolbar,
    Box,
    Button,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    Stack,
    Chip,
    Divider,
    IconButton
} from "@mui/material";

import {
    Church,
    People,
    Event,
    Favorite,
    VolunteerActivism,
    AccountBalance,
    Groups,
    AutoStories,
    Notifications,
    Security,
    BarChart,
    ArrowForward,
    CheckCircle,
    Menu
} from "@mui/icons-material";

const features = [
    {
        icon: <People />,
        title: "Member Management",
        description:
            "Manage members, profiles, family information, roles and church participation from one place."
    },
    {
        icon: <Event />,
        title: "Events & Attendance",
        description:
            "Create church events, manage attendance and keep your congregation connected."
    },
    {
        icon: <Favorite />,
        title: "Baptism & Marriage",
        description:
            "Maintain baptism and marriage records and generate certificates whenever required."
    },
    {
        icon: <AccountBalance />,
        title: "Finance & Donations",
        description:
            "Track donations, payments, expenses and financial transactions with complete visibility."
    },
    {
        icon: <VolunteerActivism />,
        title: "Volunteer Management",
        description:
            "Organize volunteers, responsibilities and ministry activities efficiently."
    },
    {
        icon: <Groups />,
        title: "Groups & Ministries",
        description:
            "Manage church groups, ministries and member participation in one centralized system."
    }
];

const benefits = [
    "Centralized church member database",
    "Baptism and marriage certificate management",
    "Donation and financial management",
    "Event and attendance tracking",
    "Staff and volunteer management",
    "SMS and communication management",
    "Role-based access control",
    "Activity and audit logs"
];

export default function Home({ onLogin }) {

    return (
        <Box
            sx={{
                minHeight: "100vh",
                background: "#fafbff",
                color: "#172033"
            }}
        >

            {/* ================= NAVBAR ================= */}

            <AppBar
                position="sticky"
                elevation={0}
                sx={{
                    background: "rgba(255,255,255,0.92)",
                    backdropFilter: "blur(12px)",
                    borderBottom: "1px solid #e8eaf0"
                }}
            >
                <Container maxWidth="xl">

                    <Toolbar
                        disableGutters
                        sx={{
                            minHeight: 72,
                            justifyContent: "space-between"
                        }}
                    >

                        {/* Logo */}

                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                        >
                            <Box
                                sx={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: 2.5,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    background:
                                        "linear-gradient(135deg,#4f46e5,#7c3aed)",
                                    color: "#fff"
                                }}
                            >
                                <Church />
                            </Box>

                            <Box>
                                <Typography
                                    fontWeight={800}
                                    fontSize={19}
                                    color="#172033"
                                >
                                    ChurchConnect
                                </Typography>

                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    Church Management System
                                </Typography>
                            </Box>
                        </Stack>

                        {/* Desktop Navigation */}

                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{
                                display: {
                                    xs: "none",
                                    md: "flex"
                                }
                            }}
                        >

                            <Button color="inherit">
                                Features
                            </Button>

                            <Button color="inherit">
                                Modules
                            </Button>

                            <Button color="inherit">
                                About
                            </Button>

                            <Button color="inherit">
                                Contact
                            </Button>

                            <Divider
                                orientation="vertical"
                                flexItem
                                sx={{ mx: 1 }}
                            />

                            <Button
                                variant="outlined"
                                onClick={onLogin}
                                sx={{
                                    borderRadius: 2,
                                    px: 2.5
                                }}
                            >
                                Login
                            </Button>

                            <Button
                                variant="contained"
                                onClick={onLogin}
                                sx={{
                                    borderRadius: 2,
                                    px: 2.5,
                                    background:
                                        "linear-gradient(135deg,#4f46e5,#7c3aed)"
                                }}
                            >
                                Get Started
                            </Button>

                        </Stack>

                        {/* Mobile */}

                        <IconButton
                            sx={{
                                display: {
                                    xs: "flex",
                                    md: "none"
                                }
                            }}
                        >
                            <Menu />
                        </IconButton>

                    </Toolbar>
                </Container>
            </AppBar>


            {/* ================= HERO ================= */}

            <Box
                sx={{
                    position: "relative",
                    overflow: "hidden",
                    background:
                        "linear-gradient(135deg,#eef2ff 0%,#faf5ff 55%,#ffffff 100%)"
                }}
            >

                <Container maxWidth="xl">

                    <Grid
                        container
                        spacing={6}
                        alignItems="center"
                        sx={{
                            minHeight: {
                                xs: 620,
                                md: 680
                            },
                            py: 8
                        }}
                    >

                        {/* Hero text */}

                        <Grid item xs={12} md={6}>

                            <Chip
                                icon={<Church />}
                                label="Modern Church Management"
                                sx={{
                                    mb: 3,
                                    fontWeight: 600,
                                    background: "#ffffff",
                                    border: "1px solid #ddd6fe"
                                }}
                            />

                            <Typography
                                component="h1"
                                sx={{
                                    fontSize: {
                                        xs: 42,
                                        md: 62
                                    },
                                    lineHeight: 1.08,
                                    fontWeight: 800,
                                    letterSpacing: "-2px"
                                }}
                            >
                                Manage your church.
                                <Box
                                    component="span"
                                    sx={{
                                        display: "block",
                                        background:
                                            "linear-gradient(90deg,#4f46e5,#9333ea)",
                                        backgroundClip: "text",
                                        WebkitBackgroundClip: "text",
                                        WebkitTextFillColor:
                                            "transparent"
                                    }}
                                >
                                    Grow your community.
                                </Box>
                            </Typography>

                            <Typography
                                sx={{
                                    mt: 3,
                                    maxWidth: 600,
                                    fontSize: 18,
                                    lineHeight: 1.7,
                                    color: "text.secondary"
                                }}
                            >
                                A complete church management platform to
                                manage members, events, attendance,
                                sacraments, donations, volunteers,
                                communication and finances from one place.
                            </Typography>

                            <Stack
                                direction={{
                                    xs: "column",
                                    sm: "row"
                                }}
                                spacing={2}
                                sx={{ mt: 4 }}
                            >

                                <Button
                                    variant="contained"
                                    size="large"
                                    endIcon={<ArrowForward />}
                                    onClick={onLogin}
                                    sx={{
                                        px: 4,
                                        py: 1.6,
                                        borderRadius: 2.5,
                                        fontSize: 16,
                                        fontWeight: 700,
                                        background:
                                            "linear-gradient(135deg,#4f46e5,#7c3aed)"
                                    }}
                                >
                                    Get Started
                                </Button>

                                <Button
                                    variant="outlined"
                                    size="large"
                                    sx={{
                                        px: 4,
                                        py: 1.6,
                                        borderRadius: 2.5,
                                        fontSize: 16
                                    }}
                                >
                                    Explore Features
                                </Button>

                            </Stack>

                            <Stack
                                direction="row"
                                spacing={3}
                                sx={{ mt: 4 }}
                                flexWrap="wrap"
                                useFlexGap
                            >
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                >
                                    <CheckCircle
                                        fontSize="small"
                                        color="success"
                                    />
                                    <Typography variant="body2">
                                        Easy to use
                                    </Typography>
                                </Stack>

                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                >
                                    <CheckCircle
                                        fontSize="small"
                                        color="success"
                                    />
                                    <Typography variant="body2">
                                        Secure
                                    </Typography>
                                </Stack>

                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                >
                                    <CheckCircle
                                        fontSize="small"
                                        color="success"
                                    />
                                    <Typography variant="body2">
                                        All-in-one platform
                                    </Typography>
                                </Stack>
                            </Stack>

                        </Grid>


                        {/* Hero Dashboard Preview */}

                        <Grid item xs={12} md={6}>

                            <Box
                                sx={{
                                    position: "relative",
                                    maxWidth: 620,
                                    mx: "auto"
                                }}
                            >

                                {/* Background decoration */}

                                <Box
                                    sx={{
                                        position: "absolute",
                                        width: 250,
                                        height: 250,
                                        borderRadius: "50%",
                                        background:
                                            "#ddd6fe",
                                        filter: "blur(70px)",
                                        top: -50,
                                        right: -50
                                    }}
                                />

                                <Card
                                    elevation={12}
                                    sx={{
                                        position: "relative",
                                        borderRadius: 4,
                                        overflow: "hidden",
                                        border:
                                            "1px solid rgba(255,255,255,.8)"
                                    }}
                                >

                                    {/* Fake browser header */}

                                    <Box
                                        sx={{
                                            px: 2,
                                            py: 1.5,
                                            borderBottom:
                                                "1px solid #eee",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 9,
                                                height: 9,
                                                borderRadius: "50%",
                                                background: "#ef4444"
                                            }}
                                        />

                                        <Box
                                            sx={{
                                                width: 9,
                                                height: 9,
                                                borderRadius: "50%",
                                                background: "#f59e0b"
                                            }}
                                        />

                                        <Box
                                            sx={{
                                                width: 9,
                                                height: 9,
                                                borderRadius: "50%",
                                                background: "#22c55e"
                                            }}
                                        />

                                    </Box>

                                    <CardContent sx={{ p: 3 }}>

                                        <Typography
                                            variant="h6"
                                            fontWeight={800}
                                        >
                                            Church Dashboard
                                        </Typography>

                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            Overview of your church
                                        </Typography>

                                        <Grid
                                            container
                                            spacing={1.5}
                                            sx={{ mt: 1 }}
                                        >

                                            {[
                                                [
                                                    "Members",
                                                    "2,548",
                                                    <People />
                                                ],
                                                [
                                                    "Events",
                                                    "18",
                                                    <Event />
                                                ],
                                                [
                                                    "Donations",
                                                    "₹1.25L",
                                                    <Favorite />
                                                ],
                                                [
                                                    "Volunteers",
                                                    "94",
                                                    <VolunteerActivism />
                                                ]
                                            ].map(
                                                ([title, value, icon]) => (
                                                    <Grid
                                                        item
                                                        xs={6}
                                                        key={title}
                                                    >
                                                        <Box
                                                            sx={{
                                                                p: 2,
                                                                borderRadius: 2.5,
                                                                background:
                                                                    "#f8fafc",
                                                                border:
                                                                    "1px solid #eef0f4"
                                                            }}
                                                        >

                                                            <Stack
                                                                direction="row"
                                                                justifyContent="space-between"
                                                            >

                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                >
                                                                    {title}
                                                                </Typography>

                                                                <Box
                                                                    sx={{
                                                                        color:
                                                                            "#6366f1"
                                                                    }}
                                                                >
                                                                    {icon}
                                                                </Box>

                                                            </Stack>

                                                            <Typography
                                                                variant="h5"
                                                                fontWeight={800}
                                                                sx={{
                                                                    mt: 1
                                                                }}
                                                            >
                                                                {value}
                                                            </Typography>

                                                        </Box>
                                                    </Grid>
                                                )
                                            )}

                                        </Grid>

                                        {/* Fake chart */}

                                        <Box
                                            sx={{
                                                mt: 2,
                                                p: 2,
                                                borderRadius: 2.5,
                                                background: "#fafafa"
                                            }}
                                        >

                                            <Typography
                                                variant="body2"
                                                fontWeight={700}
                                            >
                                                Membership Growth
                                            </Typography>

                                            <Box
                                                sx={{
                                                    height: 100,
                                                    mt: 2,
                                                    display: "flex",
                                                    alignItems: "end",
                                                    gap: 1
                                                }}
                                            >
                                                {[35, 48, 42, 58, 65, 72, 82, 92].map(
                                                    (height, index) => (
                                                        <Box
                                                            key={index}
                                                            sx={{
                                                                flex: 1,
                                                                height: `${height}%`,
                                                                borderRadius:
                                                                    "5px 5px 0 0",
                                                                background:
                                                                    "linear-gradient(to top,#6366f1,#a78bfa)"
                                                            }}
                                                        />
                                                    )
                                                )}
                                            </Box>

                                        </Box>

                                    </CardContent>
                                </Card>
                            </Box>

                        </Grid>

                    </Grid>
                </Container>
            </Box>


            {/* ================= FEATURES ================= */}

            <Container
                maxWidth="xl"
                sx={{ py: 10 }}
            >

                <Box
                    textAlign="center"
                    maxWidth={750}
                    mx="auto"
                    mb={6}
                >

                    <Chip
                        label="Everything You Need"
                        color="primary"
                        variant="outlined"
                    />

                    <Typography
                        variant="h3"
                        fontWeight={800}
                        sx={{ mt: 2 }}
                    >
                        One platform for your entire church
                    </Typography>

                    <Typography
                        color="text.secondary"
                        sx={{
                            mt: 2,
                            fontSize: 17,
                            lineHeight: 1.7
                        }}
                    >
                        Replace scattered spreadsheets and disconnected
                        systems with one centralized church management
                        platform.
                    </Typography>

                </Box>


                <Grid container spacing={3}>

                    {features.map((feature) => (

                        <Grid
                            item
                            xs={12}
                            sm={6}
                            lg={4}
                            key={feature.title}
                        >

                            <Card
                                elevation={0}
                                sx={{
                                    height: "100%",
                                    borderRadius: 3,
                                    border:
                                        "1px solid #e8eaf0",
                                    transition:
                                        "all .25s ease",
                                    "&:hover": {
                                        transform:
                                            "translateY(-6px)",
                                        boxShadow:
                                            "0 15px 35px rgba(0,0,0,.08)"
                                    }
                                }}
                            >

                                <CardContent sx={{ p: 3.5 }}>

                                    <Box
                                        sx={{
                                            width: 52,
                                            height: 52,
                                            borderRadius: 2.5,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            background:
                                                "#eef2ff",
                                            color: "#4f46e5",
                                            mb: 2.5
                                        }}
                                    >
                                        {feature.icon}
                                    </Box>

                                    <Typography
                                        variant="h6"
                                        fontWeight={750}
                                    >
                                        {feature.title}
                                    </Typography>

                                    <Typography
                                        color="text.secondary"
                                        sx={{
                                            mt: 1,
                                            lineHeight: 1.7
                                        }}
                                    >
                                        {feature.description}
                                    </Typography>

                                </CardContent>
                            </Card>

                        </Grid>

                    ))}

                </Grid>

            </Container>


            {/* ================= MODULES ================= */}

            <Box sx={{ background: "#f8f9fc", py: 10 }}>

                <Container maxWidth="xl">

                    <Grid
                        container
                        spacing={7}
                        alignItems="center"
                    >

                        <Grid item xs={12} md={6}>

                            <Chip
                                label="Complete Church Management"
                                color="primary"
                            />

                            <Typography
                                variant="h3"
                                fontWeight={800}
                                sx={{ mt: 2 }}
                            >
                                Everything connected in one place
                            </Typography>

                            <Typography
                                color="text.secondary"
                                sx={{
                                    mt: 2,
                                    lineHeight: 1.8,
                                    fontSize: 17
                                }}
                            >
                                From your first member registration to
                                financial reports, certificates,
                                communication and church activities,
                                manage everything from a single system.
                            </Typography>

                            <Grid
                                container
                                spacing={2}
                                sx={{ mt: 2 }}
                            >

                                {benefits.map((benefit) => (

                                    <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                        key={benefit}
                                    >

                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            alignItems="center"
                                        >

                                            <CheckCircle
                                                fontSize="small"
                                                color="primary"
                                            />

                                            <Typography
                                                variant="body2"
                                            >
                                                {benefit}
                                            </Typography>

                                        </Stack>

                                    </Grid>

                                ))}

                            </Grid>

                        </Grid>


                        <Grid item xs={12} md={6}>

                            <Grid container spacing={2}>

                                {[
                                    [
                                        <People />,
                                        "Members",
                                        "Manage congregation"
                                    ],
                                    [
                                        <AutoStories />,
                                        "Sacraments",
                                        "Baptism & Marriage"
                                    ],
                                    [
                                        <AccountBalance />,
                                        "Finance",
                                        "Donations & accounting"
                                    ],
                                    [
                                        <Notifications />,
                                        "Communication",
                                        "SMS & notifications"
                                    ],
                                    [
                                        <Event />,
                                        "Events",
                                        "Events & attendance"
                                    ],
                                    [
                                        <Security />,
                                        "Security",
                                        "Roles & permissions"
                                    ]
                                ].map(
                                    ([icon, title, description]) => (

                                        <Grid
                                            item
                                            xs={6}
                                            key={title}
                                        >

                                            <Card
                                                elevation={0}
                                                sx={{
                                                    p: 1,
                                                    borderRadius: 3,
                                                    border:
                                                        "1px solid #e6e8ef"
                                                }}
                                            >

                                                <CardContent>

                                                    <Box
                                                        sx={{
                                                            color:
                                                                "#4f46e5",
                                                            mb: 1
                                                        }}
                                                    >
                                                        {icon}
                                                    </Box>

                                                    <Typography
                                                        fontWeight={700}
                                                    >
                                                        {title}
                                                    </Typography>

                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        {description}
                                                    </Typography>

                                                </CardContent>

                                            </Card>

                                        </Grid>

                                    )
                                )}

                            </Grid>

                        </Grid>

                    </Grid>

                </Container>

            </Box>


            {/* ================= CTA ================= */}

            <Container
                maxWidth="lg"
                sx={{ py: 10 }}
            >

                <Box
                    sx={{
                        borderRadius: 5,
                        p: {
                            xs: 4,
                            md: 7
                        },
                        textAlign: "center",
                        background:
                            "linear-gradient(135deg,#312e81,#6d28d9)",
                        color: "#fff"
                    }}
                >

                    <Typography
                        variant="h3"
                        fontWeight={800}
                    >
                        Ready to simplify your church management?
                    </Typography>

                    <Typography
                        sx={{
                            mt: 2,
                            opacity: .85,
                            maxWidth: 650,
                            mx: "auto",
                            fontSize: 17
                        }}
                    >
                        Bring your members, ministries, events,
                        finances and communication together in one
                        powerful platform.
                    </Typography>

                    <Button
                        variant="contained"
                        size="large"
                        onClick={onLogin}
                        endIcon={<ArrowForward />}
                        sx={{
                            mt: 4,
                            px: 4,
                            py: 1.5,
                            borderRadius: 2.5,
                            background: "#fff",
                            color: "#4f46e5",
                            fontWeight: 700,
                            "&:hover": {
                                background: "#f5f5f5"
                            }
                        }}
                    >
                        Get Started
                    </Button>

                </Box>

            </Container>


            {/* ================= FOOTER ================= */}

            <Box
                sx={{
                    background: "#111827",
                    color: "#fff",
                    py: 6
                }}
            >

                <Container maxWidth="xl">

                    <Grid
                        container
                        spacing={4}
                    >

                        <Grid item xs={12} md={5}>

                            <Stack
                                direction="row"
                                spacing={1.5}
                                alignItems="center"
                            >

                                <Box
                                    sx={{
                                        width: 42,
                                        height: 42,
                                        borderRadius: 2,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background: "#4f46e5"
                                    }}
                                >
                                    <Church />
                                </Box>

                                <Typography
                                    variant="h6"
                                    fontWeight={800}
                                >
                                    ChurchConnect
                                </Typography>

                            </Stack>

                            <Typography
                                sx={{
                                    mt: 2,
                                    maxWidth: 450,
                                    color: "#9ca3af",
                                    lineHeight: 1.7
                                }}
                            >
                                A modern church management platform
                                designed to help churches manage
                                people, ministries, events and finances.
                            </Typography>

                        </Grid>

                        <Grid item xs={6} md={2}>

                            <Typography
                                fontWeight={700}
                                sx={{ mb: 2 }}
                            >
                                Platform
                            </Typography>

                            <Stack spacing={1}>

                                <Typography color="#9ca3af">
                                    Features
                                </Typography>

                                <Typography color="#9ca3af">
                                    Modules
                                </Typography>

                                <Typography color="#9ca3af">
                                    Security
                                </Typography>

                                <Typography color="#9ca3af">
                                    Pricing
                                </Typography>

                            </Stack>

                        </Grid>

                        <Grid item xs={6} md={2}>

                            <Typography
                                fontWeight={700}
                                sx={{ mb: 2 }}
                            >
                                Company
                            </Typography>

                            <Stack spacing={1}>

                                <Typography color="#9ca3af">
                                    About
                                </Typography>

                                <Typography color="#9ca3af">
                                    Contact
                                </Typography>

                                <Typography color="#9ca3af">
                                    Privacy
                                </Typography>

                                <Typography color="#9ca3af">
                                    Terms
                                </Typography>

                            </Stack>

                        </Grid>

                        <Grid item xs={12} md={3}>

                            <Typography
                                fontWeight={700}
                                sx={{ mb: 2 }}
                            >
                                Account
                            </Typography>

                            <Button
                                variant="outlined"
                                onClick={onLogin}
                                sx={{
                                    color: "#fff",
                                    borderColor: "#4b5563",
                                    borderRadius: 2
                                }}
                            >
                                Login
                            </Button>

                        </Grid>

                    </Grid>

                    <Divider
                        sx={{
                            my: 5,
                            borderColor: "#374151"
                        }}
                    />

                    <Typography
                        variant="body2"
                        color="#9ca3af"
                        textAlign="center"
                    >
                        © {new Date().getFullYear()} ChurchConnect.
                        All rights reserved.
                    </Typography>

                </Container>

            </Box>

        </Box>
    );
}